// Tiering. The control sits on the volume, not on the rate.
//
// One card, and the readout IS the control: the Leads bar shows where the tier
// boundaries fall and the handles on it are how they move. Splitting the two
// produced a read-only copy of the same bar above a second interactive one, which
// is two pictures of one fact.
//
// The Conversions bar underneath is the other half of the reading, and the pair
// is the point. A tier holding most of the conversions may be doing so only by
// holding most of the leads, which is visible as the two bars lining up rather
// than as a number anywhere. The headline states the one comparison worth
// reading without hunting: what share of leads the qualified tiers hold against
// what share of conversions they carry.
//
// Handles are drawn where the boundaries ACTUALLY fall. A request that had to
// snap to the nearest score shows where it landed, while the request itself stays
// in the page's state so a later weight change re-derives from what was asked
// for rather than from what was settled for.
import React from 'react';
import { observer } from 'mobx-react';

import DraftNumberInput from './DraftNumberInput';
import {
  BOUNDED_TIERS,
  LadderRung,
  rangeHint,
  nearestShare,
  shareLabels,
  thresholdRange,
  TierOutcome,
} from './scoreLadder';
import './tieringPanel.css';

/** Below this the band is narrower than its own name. */
const MIN_NAME = 0.1;

/**
 * Below this many pixels the band is narrower than its own number ("100%" at
 * 0.8rem plus the band's padding), and the number moves under the band instead.
 * Measured in pixels rather than as a share, because a share cannot know the
 * bar's width: 5% of a wide screen is 90px of empty band.
 */
const MIN_VALUE_PX = 36;
/** The share rule, for the first render, before the bar has been measured. */
const MIN_VALUE = 0.05;
/** A callout this close to either end of the bar is pinned to that end. */
const CALLOUT_EDGE_PX = 24;

/**
 * The narrowest a band is DRAWN, as a share of the bar, where a page asks for
 * it. A segment holding 1% of leads, or none, still gets room for its number
 * and a gap between the handles either side of it, so a tier never disappears
 * and two handles never stack. The widths are a picture; every number printed
 * is the real share. The engagement page asks; fit draws true widths.
 */
export const MIN_VISIBLE = 0.06;

/**
 * Past this many rungs the tick strip is a solid grey block, which says only
 * "dense" and costs a row of the card to say it.
 */
const MAX_TICKS = 48;

/**
 * Closer than this, two boundary fields would overlap on one row, so the later
 * one drops to a second row. A share of the bar, sized for a four digit total.
 */
const FIELD_GAP = 0.14;

const rate = (value: number) => `${value.toFixed(1)}%`;
const count = (value: number) => value.toLocaleString('en-US');

/**
 * The row each boundary field goes on: the first row where it clears the last
 * field already placed there. Compared only with its neighbour, the first and
 * third of three close handles both took row 0 and overlapped.
 */
/**
 * Drawn widths: every share at least `min`, the rest scaled into what is left
 * in proportion. Repeated because scaling can pull another band under the floor.
 */
export function visibleWidths(shares: number[], min = MIN_VISIBLE): number[] {
  const floor = Math.min(min, 1 / Math.max(1, shares.length));
  const pinned = new Set<number>();
  for (;;) {
    const free = 1 - pinned.size * floor;
    const rest = shares.reduce(
      (sum, share, index) => (pinned.has(index) ? sum : sum + share),
      0
    );
    const widths = shares.map((share, index) =>
      pinned.has(index)
        ? floor
        : rest > 0
        ? (share / rest) * free
        : free / (shares.length - pinned.size)
    );
    const under = widths.findIndex(
      (width, index) => !pinned.has(index) && width < floor - 1e-12
    );
    if (under === -1) return widths;
    pinned.add(under);
  }
}

/** Running totals with a leading 0: [0, a, a+b, ..., 1]. */
const edgesOf = (widths: number[]) =>
  widths.reduce((out, width) => [...out, out[out.length - 1] + width], [0]);

/**
 * A position on one set of edges to the same relative place on another, band
 * by band. Used both ways: real share to drawn position for the ticks, drawn
 * position to real share for the pointer.
 */
export function mapAcross(value: number, from: number[], to: number[]): number {
  const last = from.length - 1;
  const band = Math.max(
    0,
    Math.min(
      last - 1,
      from.findIndex((edge, index) => index < last && value <= from[index + 1])
    )
  );
  const span = from[band + 1] - from[band];
  const along = span > 0 ? (value - from[band]) / span : 0;
  return to[band] + Math.max(0, Math.min(1, along)) * (to[band + 1] - to[band]);
}

export function fieldRowsFor(edges: number[], gap: number): number[] {
  const lastOnRow: number[] = [];
  return edges.map((edge) => {
    const row = lastOnRow.findIndex((last) => edge - last >= gap);
    const placed = row === -1 ? lastOnRow.length : row;
    lastOnRow[placed] = edge;
    return placed;
  });
}

function TieringPanel({
  tiers,
  bounds,
  ladder,
  onChangeBounds,
  disabled,
  thresholdInput,
  minVisible,
}: {
  /** Best first, four of them, as produced by the thresholds now in the model. */
  tiers: TierOutcome[];
  /**
   * The requested cumulative shares. Carried in so a drag rewrites only the
   * boundary it moved and clamps against the other requests, rather than against
   * where they happen to have landed. Rebuilding the set from achieved values is
   * how the prototype's tiers drifted away from the request.
   */
  bounds: number[];
  ladder: LadderRung[];
  onChangeBounds: (bounds: number[]) => void;
  disabled?: boolean;
  /**
   * A typed threshold under each handle. Offered where the score is a number a
   * person reasons in, which is a point total; on a tree based model the handle
   * is the control and the threshold is its consequence, so there is none.
   */
  thresholdInput?: {
    step: number;
    suffix: string;
    onChange: (segment: string, value: number) => void;
  };
  /** Draw every band at least this wide and always print its share. */
  minVisible?: number;
}) {
  const barRef = React.useRef<HTMLDivElement>(null);
  // Both bars span the same width, so one measurement serves both.
  const [barWidth, setBarWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    setBarWidth(bar.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setBarWidth(entry.contentRect.width)
    );
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);
  const fitsValue = (share: number) =>
    barWidth > 0 ? share * barWidth >= MIN_VALUE_PX : share > MIN_VALUE;

  // A band too narrow for its number still owns it: the number is printed under
  // the band, centred on it, never dropped. Both are judged on the DRAWN
  // widths, so a band the engagement page widens to MIN_VISIBLE shows its
  // number inside rather than in a callout as well. Hiding it left a red sliver whose
  // share only a hover could tell, on the one band people look for.
  const callouts = (shares: number[], labels: number[]) => {
    const narrow = shares
      .map((share, index) => {
        const start = shares
          .slice(0, index)
          .reduce((sum, each) => sum + each, 0);
        return { index, share, centre: start + share / 2 };
      })
      .filter(({ share }) => share > 0 && !fitsValue(share));
    if (!narrow.length) return null;
    return (
      <div className="tier-callouts">
        {narrow.map(({ index, centre }) => {
          const px = centre * barWidth;
          const atStart = barWidth > 0 && px < CALLOUT_EDGE_PX;
          const atEnd = barWidth > 0 && barWidth - px < CALLOUT_EDGE_PX;
          return (
            <span
              key={tiers[index].segment}
              className={`tier-callout${atStart ? ' tier-callout--start' : ''}${
                atEnd ? ' tier-callout--end' : ''
              }`}
              style={
                atEnd
                  ? { right: 0 }
                  : { left: atStart ? 0 : `${centre * 100}%` }
              }
            >
              <i style={{ background: tiers[index].color ?? undefined }} />
              {labels[index]}%
            </span>
          );
        })}
      </div>
    );
  };

  // Largest remainder, so the labels on each bar total 100 and never read as 99.
  const leadLabels = shareLabels(tiers.map((tier) => tier.population));
  const conversionLabels = shareLabels(tiers.map((tier) => tier.conversions));

  // Cumulative achieved share at each boundary. Four tiers, three boundaries.
  const edges = tiers
    .slice(0, -1)
    .map((tier, index) =>
      tiers.slice(0, index + 1).reduce((sum, each) => sum + each.share, 0)
    );
  // Where the bands are drawn, which is not quite where they fall: see
  // MIN_VISIBLE. Handles, fields and ticks all sit on the drawn edges.
  const widthsOf = (shares: number[]) =>
    minVisible ? visibleWidths(shares, minVisible) : shares;
  const widths = widthsOf(tiers.map((tier) => tier.share));
  const realEdges = [0, ...edges, 1];
  const drawnEdges = edgesOf(widths);
  const drawn = drawnEdges.slice(1, -1);
  const convWidths = widthsOf(tiers.map((tier) => tier.conversionShare));
  // The tiers as this page names them, for a refused field's reason.
  const labelOf = (tier: string) =>
    tiers.find((each) => each.segment === tier)?.label ?? tier;

  // The thresholds as the tiers carry them, which is what the fields show and
  // what each field's range is taken against.
  const typed = BOUNDED_TIERS.reduce((out, segment, index) => {
    out[segment] = Number(tiers[index]?.threshold);
    return out;
  }, {} as Record<string, number>);
  const fieldRows = fieldRowsFor(drawn, FIELD_GAP);
  // The field holding back what is typed, so it can say why.
  const [refused, setRefused] = React.useState<string | null>(null);

  const move = (index: number, target: number) => {
    const clamped = Math.max(0, Math.min(1, target));
    // Against the neighbouring REQUESTS, so a boundary cannot cross one that has
    // not been re-requested, and strictly inside them so two can never stack.
    const landed = nearestShare(
      clamped,
      ladder,
      index > 0 ? bounds[index - 1] : 0,
      index < bounds.length - 1 ? bounds[index + 1] : 1
    );
    // No lead sits in the gap, so the handle holds rather than snapping
    // somewhere it was not dragged to.
    if (landed === null || landed === bounds[index]) return;

    const next = bounds.slice();
    next[index] = landed;
    onChangeBounds(next);
  };

  /** Arrow keys step to the adjacent rung, not by a fixed percentage. The rungs
   *  are what a boundary may occupy, so a step of anything else would either
   *  skip reachable splits or land back where it started. */
  const step = (index: number, direction: number) => {
    const ordered = ladder.map((rung) => rung.share).sort((a, b) => a - b);
    const at = ordered.findIndex(
      (share) => Math.abs(share - edges[index]) < 1e-9
    );
    const next = ordered[at + direction];
    if (next != null) move(index, next);
  };

  const shareAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect?.width) return 0;
    return (clientX - rect.left) / rect.width;
  };

  return (
    <div className="tier-grid">
      <div className="tier-main">
        {/* Read off the labels the bars below actually print, not off a second
            rounding of the same shares. Summing the exact values and rounding
            once said "81% of conversions" over a bar reading 48 and 32. */}
        <p className="tier-head">
          <b>{leadLabels[0] + leadLabels[1]}%</b> of leads (
          {tiers[0].label.toLowerCase()} and {tiers[1].label.toLowerCase()})
          make <b>{conversionLabels[0] + conversionLabels[1]}%</b> of
          conversions.
        </p>

        <p className="tier-barlab">Leads</p>
        <div className="tier-wrap">
          <div className="tier-bar" ref={barRef}>
            {tiers.map((tier, index) => (
              <div
                key={tier.segment}
                className="tier-seg"
                style={{
                  width: `${widths[index] * 100}%`,
                  background: tier.color,
                }}
                title={`${tier.label}: ${count(tier.population)} leads, ${
                  leadLabels[index]
                }% of the population, converting at ${rate(tier.rate)}`}
              >
                {widths[index] > MIN_NAME && (
                  <span
                    className="tier-seg-name"
                    style={{ color: tier.textColor }}
                  >
                    {tier.label}
                  </span>
                )}
                {fitsValue(widths[index]) && (
                  <span
                    className="tier-seg-value"
                    style={{ color: tier.textColor }}
                  >
                    {leadLabels[index]}%
                  </span>
                )}
              </div>
            ))}
          </div>

          {drawn.map((edge, index) => (
            <button
              type="button"
              key={`edge_${tiers[index].segment}`}
              className="tier-handle"
              style={{ left: `${edge * 100}%` }}
              role="slider"
              aria-label={`${tiers[index].label} boundary`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(edges[index] * 100)}
              aria-valuetext={`${rate(
                edges[index] * 100
              )} of leads above this point`}
              disabled={disabled}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={(event) => {
                if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
                  return;
                }
                move(
                  index,
                  mapAcross(shareAt(event.clientX), drawnEdges, realEdges)
                );
              }}
              onPointerUp={(event) => {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }}
              onKeyDown={(event) => {
                const direction =
                  event.key === 'ArrowLeft'
                    ? -1
                    : event.key === 'ArrowRight'
                    ? 1
                    : 0;
                if (!direction) return;
                event.preventDefault();
                step(index, direction);
              }}
            />
          ))}
        </div>
        {callouts(widths, leadLabels)}

        {/* Every score a boundary can occupy. This is the difference between a
            handle that slides and one that steps, and without it the snapping
            reads as the control fighting the pointer. */}
        <div className="tier-ticks">
          {ladder.length <= MAX_TICKS &&
            ladder.map((rung) => (
              <span
                key={rung.score}
                className="tier-tick"
                style={{
                  left: `${
                    mapAcross(rung.share, realEdges, drawnEdges) * 100
                  }%`,
                }}
              />
            ))}
        </div>
        {thresholdInput && (
          <div
            className="tier-fields"
            style={{ height: `${(Math.max(0, ...fieldRows) + 1) * 32}px` }}
          >
            {drawn.map((edge, index) => {
              const segment = BOUNDED_TIERS[index];
              const range = thresholdRange(segment, typed, thresholdInput.step);
              const value = tiers[index]?.threshold;
              return (
                <label
                  key={`field_${segment}`}
                  className="tier-field"
                  style={{
                    left: `${edge * 100}%`,
                    top: `${fieldRows[index] * 32}px`,
                  }}
                >
                  <DraftNumberInput
                    step={thresholdInput.step}
                    min={range.min}
                    max={range.max}
                    value={value}
                    disabled={disabled}
                    aria-label={`${tiers[index].label} threshold`}
                    data-testid={`tiering-threshold-${segment.replace(
                      ' ',
                      '-'
                    )}`}
                    onCommit={(next) => thresholdInput.onChange(segment, next)}
                    onRefusedChange={(isRefused) =>
                      setRefused((current) =>
                        isRefused
                          ? segment
                          : current === segment
                          ? null
                          : current
                      )
                    }
                  />
                  <span>{thresholdInput.suffix}</span>
                  {refused === segment && (
                    <span className="tier-field-hint" role="status">
                      {rangeHint(segment, typed, labelOf)}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        <div className="tier-ends">
          <span>Best converting</span>
          <span>Worst converting</span>
        </div>

        <p className="tier-barlab tier-barlab--spaced">Conversions</p>
        <div className="tier-convbar">
          {tiers.map((tier, index) => (
            <div
              key={tier.segment}
              className="tier-convseg"
              style={{
                width: `${convWidths[index] * 100}%`,
                background: tier.color,
              }}
              title={`${tier.label}: ${count(tier.conversions)} conversions, ${
                conversionLabels[index]
              }% of all conversions`}
            >
              {fitsValue(convWidths[index]) && (
                <span
                  className="tier-seg-value"
                  style={{ color: tier.textColor }}
                >
                  {conversionLabels[index]}%
                </span>
              )}
            </div>
          ))}
        </div>
        {callouts(convWidths, conversionLabels)}
      </div>
    </div>
  );
}

export default observer(TieringPanel);
